import React, { useState, useEffect } from 'react';
import { Layout } from '@/components/Layout';
import { API, useAuth } from '@/App';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Save, ArrowLeft, Loader2, Plus, X, DollarSign } from 'lucide-react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Combobox } from "@/components/ui/combobox";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { FamiliaAtencionSection } from "@/components/FamiliaAtencionSection";
import { parseRniText } from "@/lib/rniParser";

const NewVaccinationPage = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const isEditing = !!id;
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [staff, setStaff] = useState([]);
  const [empresas, setEmpresas] = useState([]);
  const [inventarioLotes, setInventarioLotes] = useState([]);
  const [catalogs, setCatalogs] = useState({
    comunas: [],
    tipo_vacunacion: [],
    dosis: [],
    vacuna: [],
    tipo_pago: []
  });
  const [vacunasConPrecio, setVacunasConPrecio] = useState([]); // Full vaccine objects with prices and doses
  const [vacunasSeleccionadas, setVacunasSeleccionadas] = useState([]); // Selected vaccines array
  const [totalSugerido, setTotalSugerido] = useState(0);
  const [dosisDisponibles, setDosisDisponibles] = useState([]); // Available doses based on selected vaccines
  const [edadCalculada, setEdadCalculada] = useState(null); // Calculated age from birth date
  const [rniText, setRniText] = useState(''); // Text pasted from RNI
  const [rniParsed, setRniParsed] = useState(false); // Flag to show if RNI was parsed
  const [buscandoPaciente, setBuscandoPaciente] = useState(false); // Loading state for patient search
  const [pacienteEncontrado, setPacienteEncontrado] = useState(false); // Flag if patient was found
  const [familiaMiembros, setFamiliaMiembros] = useState([]); // Family members of found patient
  const [miembrosAtencion, setMiembrosAtencion] = useState([]); // Members added to this attention
  const [originalRecord, setOriginalRecord] = useState(null); // Existing record loaded for edit (borrador or vacunado)

  const [formData, setFormData] = useState({
    fecha_vacunacion: new Date().toISOString().split('T')[0],
    rut: '',
    pasaporte: '',
    otro_documento: '',
    nombres: '',
    primer_apellido: '',
    segundo_apellido: '',
    fecha_nacimiento: '',
    comuna: '',
    tipo_vacunacion: '',
    dosis: '',
    vacuna: '',
    vacunas: [], // Array of vaccines
    lote_serie: '',
    inventario_id: '',
    telefono: '',
    email: '',
    monto: 0,
    tipo_pago: '',
    numero_boleta: '',
    administracion: '',
    registro_rni: '',
    comentarios: '',
    empresa: '',
    empresa_id: ''
  });

  useEffect(() => {
    fetchCatalogs();
    fetchStaff();
    fetchEmpresas();
    if (isEditing) {
      fetchVaccination();
    } else {
      // Prefill RUT from query string (e.g. coming from /historial)
      const prefillRut = searchParams.get('rut');
      if (prefillRut) {
        setFormData(prev => ({ ...prev, rut: prefillRut }));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // Auto-select staff member if user is linked
  useEffect(() => {
    if (!isEditing && staff.length > 0 && user && !formData.administracion) {
      // Try to find staff member by user email or name match
      const matchedStaff = staff.find(s => 
        s.email === user.email || 
        s.user_id === user.user_id ||
        (user.name && s.nombre_completo.toLowerCase().includes(user.name.split(' ')[0].toLowerCase()))
      );
      if (matchedStaff) {
        setFormData(prev => ({...prev, administracion: matchedStaff.nombre_completo}));
      }
    }
  }, [staff, user, isEditing, formData.administracion]);

  // Fetch available lots when vaccine changes
  useEffect(() => {
    if (formData.vacuna) {
      fetchInventarioLotes(formData.vacuna);
    } else {
      setInventarioLotes([]);
    }
  }, [formData.vacuna]);

  // Calculate total price and available doses when vaccines change
  useEffect(() => {
    const total = vacunasSeleccionadas.reduce((sum, v) => sum + (v.precio || 0), 0);
    setTotalSugerido(total);

    // Calculate available doses - intersection of all selected vaccines' doses
    if (vacunasSeleccionadas.length > 0) {
      // Get all doses from selected vaccines that have specific doses defined
      const vacunasConDosis = vacunasSeleccionadas.filter(v => v.dosis_disponibles && v.dosis_disponibles.length > 0);
      
      if (vacunasConDosis.length > 0) {
        // If any vaccine has specific doses, use intersection
        let commonDoses = vacunasConDosis[0].dosis_disponibles;
        for (let i = 1; i < vacunasConDosis.length; i++) {
          commonDoses = commonDoses.filter(d => vacunasConDosis[i].dosis_disponibles.includes(d));
        }
        setDosisDisponibles(commonDoses);
      } else {
        // No vaccine has specific doses, use general doses from catalog
        setDosisDisponibles(catalogs.dosis || []);
      }
    } else {
      // No vaccines selected, use general doses
      setDosisDisponibles(catalogs.dosis || []);
    }
  }, [vacunasSeleccionadas, catalogs.dosis]);

  // Calculate age when birth date changes
  useEffect(() => {
    if (formData.fecha_nacimiento) {
      const edad = calcularEdad(formData.fecha_nacimiento);
      setEdadCalculada(edad);
    } else {
      setEdadCalculada(null);
    }
    // calcularEdad is a stable pure function (no state/props closure) defined below.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.fecha_nacimiento]);

  // Function to calculate age in years and months
  const calcularEdad = (fechaNacimiento) => {
    const hoy = new Date();
    const nacimiento = new Date(fechaNacimiento);
    
    if (isNaN(nacimiento.getTime())) return null;
    
    let años = hoy.getFullYear() - nacimiento.getFullYear();
    let meses = hoy.getMonth() - nacimiento.getMonth();
    let dias = hoy.getDate() - nacimiento.getDate();
    
    // Adjust for negative days
    if (dias < 0) {
      meses--;
      // Get days in previous month
      const lastMonth = new Date(hoy.getFullYear(), hoy.getMonth(), 0);
      dias += lastMonth.getDate();
    }
    
    // Adjust for negative months
    if (meses < 0) {
      años--;
      meses += 12;
    }
    
    // Calculate total months for babies
    const totalMeses = años * 12 + meses;
    
    return {
      años,
      meses,
      dias,
      totalMeses,
      texto: formatearEdad(años, meses, dias)
    };
  };

  const formatearEdad = (años, meses, dias) => {
    const partes = [];
    
    if (años > 0) {
      partes.push(`${años} ${años === 1 ? 'año' : 'años'}`);
    }
    
    if (meses > 0) {
      partes.push(`${meses} ${meses === 1 ? 'mes' : 'meses'}`);
    }
    
    // Show days only for babies under 1 month
    if (años === 0 && meses === 0) {
      partes.push(`${dias} ${dias === 1 ? 'día' : 'días'}`);
    }
    
    return partes.length > 0 ? partes.join(' y ') : '0 días';
  };

  // Parse RNI pasted text and fill form fields
  // Format: RUT\t\tNOMBRES\tPRIMER_APELLIDO\tSEGUNDO_APELLIDO\tFECHA_NACIMIENTO
  // Example: 193185878			GUSTAVO DAVID	JALAF	RODRIGUEZ	11-02-1996
  const parsearRNI = (texto) => {
    if (!texto.trim()) {
      setRniParsed(false);
      return;
    }
    try {
      const datosExtraidos = parseRniText(texto) || {};
      if (Object.keys(datosExtraidos).length > 0) {
        setFormData(prev => ({ ...prev, ...datosExtraidos }));
        setRniParsed(true);
        const labels = {
          rut: 'RUT', nombres: 'Nombres', primer_apellido: 'Primer Apellido',
          segundo_apellido: 'Segundo Apellido', fecha_nacimiento: 'Fecha Nacimiento',
        };
        const camposExtraidos = Object.keys(datosExtraidos).map(k => labels[k]).filter(Boolean);
        toast.success(`Datos extraídos: ${camposExtraidos.join(', ')}`);
      } else {
        toast.info('No se pudieron extraer datos. Verifique el formato del texto pegado.');
        setRniParsed(false);
      }
    } catch (error) {
      console.error('Error parsing RNI:', error);
      toast.error('Error al procesar los datos del RNI');
      setRniParsed(false);
    }
  };

  const limpiarRNI = () => {
    setRniText('');
    setRniParsed(false);
  };

  // Search patient by RUT when RUT changes (with debounce)
  // Triggers as soon as we have at least 7 chars (digits+optional DV), not just formatted "X-Y".
  useEffect(() => {
    const cleaned = (formData.rut || '').replace(/[^0-9kK]/gi, '').toUpperCase();

    if (cleaned && cleaned.length >= 7) {
      const timeoutId = setTimeout(() => {
        buscarPacientePorRut(formData.rut.trim());
      }, 500);
      return () => clearTimeout(timeoutId);
    } else {
      setPacienteEncontrado(false);
    }
    // buscarPacientePorRut is declared below and only closes over isEditing
    // and setters which are stable. Adding it would cause a redefine loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.rut]);

  const buscarPacientePorRut = async (rut) => {
    if (isEditing) return; // Don't search when editing
    
    setBuscandoPaciente(true);
    try {
      const response = await axios.get(`${API}/pacientes/buscar?rut=${encodeURIComponent(rut)}`, {
        withCredentials: true
      });
      
      if (response.data.encontrado && response.data.paciente) {
        const paciente = response.data.paciente;
        
        // Fill form with patient data, auto-formatting RUT
        setFormData(prev => ({
          ...prev,
          rut: paciente.rut || prev.rut,
          pasaporte: paciente.pasaporte || prev.pasaporte,
          otro_documento: paciente.otro_documento || prev.otro_documento,
          nombres: paciente.nombres || prev.nombres,
          primer_apellido: paciente.primer_apellido || prev.primer_apellido,
          segundo_apellido: paciente.segundo_apellido || prev.segundo_apellido,
          fecha_nacimiento: paciente.fecha_nacimiento || prev.fecha_nacimiento,
          comuna: paciente.comuna || prev.comuna,
          telefono: paciente.telefono || prev.telefono,
          email: paciente.email || prev.email,
          empresa: paciente.empresa || prev.empresa,
          empresa_id: paciente.empresa_id || prev.empresa_id
        }));
        
        setPacienteEncontrado(true);
        const origenLabel = {
          pacientes: 'base de datos',
          pacientes_empresa: 'base de empresa',
          vaccinations: 'historial'
        }[paciente.origen] || 'registros';
        toast.success(`Paciente encontrado en ${origenLabel} - datos cargados`);

        // Fetch family members if the patient belongs to a family
        if (paciente.id && paciente.familia_id && (paciente.origen === 'pacientes' || paciente.origen === 'pacientes_empresa')) {
          fetchFamilia(paciente.id, paciente.origen === 'pacientes_empresa' ? 'empresa' : 'general');
        } else {
          setFamiliaMiembros([]);
          setMiembrosAtencion([]);
        }
      } else {
        setPacienteEncontrado(false);
        setFamiliaMiembros([]);
        setMiembrosAtencion([]);
      }
    } catch (error) {
      console.error('Error searching patient:', error);
      setPacienteEncontrado(false);
    } finally {
      setBuscandoPaciente(false);
    }
  };

  const fetchFamilia = async (pacienteId, tipoPaciente) => {
    try {
      const r = await axios.get(`${API}/pacientes/${pacienteId}/familia`, {
        params: { tipo: tipoPaciente }, withCredentials: true
      });
      setFamiliaMiembros(r.data.miembros || []);
      if ((r.data.miembros || []).length > 0) {
        toast.info(`Este paciente tiene ${r.data.miembros.length} familiar(es) registrado(s). Puedes agregarlos a esta atención.`);
      }
    } catch (error) {
      console.error('Error fetching family:', error);
      setFamiliaMiembros([]);
    }
  };

  const fetchStaff = async () => {
    try {
      const response = await axios.get(`${API}/staff?active_only=true`, {
        withCredentials: true
      });
      setStaff(response.data);
    } catch (error) {
      console.error('Error fetching staff:', error);
    }
  };

  const fetchEmpresas = async () => {
    try {
      const response = await axios.get(`${API}/empresas?active_only=true`, {
        withCredentials: true
      });
      setEmpresas(response.data);
    } catch (error) {
      console.error('Error fetching empresas:', error);
    }
  };

  const fetchInventarioLotes = async (vacuna) => {
    try {
      const response = await axios.post(`${API}/inventario/seleccionar-lote?vacuna=${encodeURIComponent(vacuna)}`, {}, {
        withCredentials: true
      });
      setInventarioLotes(response.data);
    } catch (error) {
      console.error('Error fetching lotes:', error);
      setInventarioLotes([]);
    }
  };

  const fetchCatalogs = async () => {
    try {
      const response = await axios.get(`${API}/catalogs`, {
        withCredentials: true
      });
      
      const grouped = response.data.reduce((acc, item) => {
        if (!acc[item.category]) acc[item.category] = [];
        acc[item.category].push(item.value);
        return acc;
      }, {});

      // Store full vaccine objects with prices
      const vacunas = response.data.filter(item => item.category === 'vacuna');
      setVacunasConPrecio(vacunas);

      setCatalogs(prev => ({...prev, ...grouped}));
    } catch (error) {
      // If no catalogs exist, seed them
      try {
        await axios.post(`${API}/seed/catalogs`, {}, { withCredentials: true });
        fetchCatalogs();
      } catch (seedError) {
        console.error('Error seeding catalogs:', seedError);
      }
    }
  };

  const fetchVaccination = async () => {
    try {
      // Try borrador endpoint first (single record), fallback to list
      let vacc = null;
      try {
        const single = await axios.get(`${API}/vaccinations/borradores/${id}`, { withCredentials: true });
        vacc = single.data;
      } catch {
        const response = await axios.get(`${API}/vaccinations?limit=1000`, { withCredentials: true });
        vacc = response.data.find(v => v.id === id);
      }
      if (vacc) {
        setOriginalRecord(vacc);
        setFormData({
          fecha_vacunacion: vacc.fecha_vacunacion || '',
          rut: vacc.rut || '',
          pasaporte: vacc.pasaporte || '',
          otro_documento: vacc.otro_documento || '',
          nombres: vacc.nombres || '',
          primer_apellido: vacc.primer_apellido || '',
          segundo_apellido: vacc.segundo_apellido || '',
          fecha_nacimiento: vacc.fecha_nacimiento || '',
          comuna: vacc.comuna || '',
          tipo_vacunacion: vacc.tipo_vacunacion || '',
          dosis: vacc.dosis || '',
          vacuna: vacc.vacuna || '',
          lote_serie: vacc.lote_serie || '',
          telefono: vacc.telefono || '',
          email: vacc.email || '',
          monto: vacc.monto || 0,
          tipo_pago: vacc.tipo_pago || '',
          numero_boleta: vacc.numero_boleta || '',
          administracion: vacc.administracion || '',
          registro_rni: vacc.registro_rni || '',
          comentarios: vacc.comentarios || '',
          empresa: vacc.empresa || ''
        });
      }
    } catch (error) {
      toast.error('Error al cargar la vacunación');
      navigate('/vaccinations');
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({...prev, [field]: value}));
  };

  const handleAddVacuna = (vacunaName) => {
    if (!vacunaName || vacunasSeleccionadas.find(v => v.value === vacunaName)) return;
    
    const vacunaObj = vacunasConPrecio.find(v => v.value === vacunaName);
    if (vacunaObj) {
      // Add vaccine with empty dosis selection
      const nuevaVacuna = {
        ...vacunaObj,
        dosisSeleccionada: '' // Each vaccine has its own dose selection
      };
      const nuevasVacunas = [...vacunasSeleccionadas, nuevaVacuna];
      setVacunasSeleccionadas(nuevasVacunas);
      // Update formData
      setFormData(prev => ({
        ...prev, 
        vacuna: nuevasVacunas.map(v => v.value).join(', '),
        vacunas: nuevasVacunas.map(v => ({ nombre: v.value, dosis: v.dosisSeleccionada }))
      }));
    }
  };

  const handleRemoveVacuna = (vacunaName) => {
    const nuevasVacunas = vacunasSeleccionadas.filter(v => v.value !== vacunaName);
    setVacunasSeleccionadas(nuevasVacunas);
    setFormData(prev => ({
      ...prev, 
      vacuna: nuevasVacunas.map(v => v.value).join(', '),
      vacunas: nuevasVacunas.map(v => ({ nombre: v.value, dosis: v.dosisSeleccionada }))
    }));
  };

  const handleVacunaDosisChange = (vacunaName, dosis) => {
    const nuevasVacunas = vacunasSeleccionadas.map(v => 
      v.value === vacunaName ? { ...v, dosisSeleccionada: dosis } : v
    );
    setVacunasSeleccionadas(nuevasVacunas);
    setFormData(prev => ({
      ...prev, 
      vacunas: nuevasVacunas.map(v => ({ nombre: v.value, dosis: v.dosisSeleccionada }))
    }));
  };

  const aplicarTotalSugerido = () => {
    setFormData(prev => ({...prev, monto: totalSugerido}));
  };

  const formatCurrency = (value) => {
    if (!value) return '$0';
    return new Intl.NumberFormat('es-CL', { style: 'currency', currency: 'CLP' }).format(value);
  };

  // Check if all vaccines have their dose selected
  const todasVacunasTienenDosis = () => {
    return vacunasSeleccionadas.every(v => v.dosisSeleccionada)
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!formData.nombres || !formData.primer_apellido) {
      toast.error('Nombre y primer apellido son requeridos');
      return;
    }
    if (!formData.fecha_nacimiento) {
      toast.error('Fecha de nacimiento es requerida');
      return;
    }
    if (vacunasSeleccionadas.length === 0) {
      toast.error('Debe seleccionar al menos una vacuna');
      return;
    }
    // Check that all vaccines have dose selected
    const vacunasSinDosis = vacunasSeleccionadas.filter(v => !v.dosisSeleccionada);
    if (vacunasSinDosis.length > 0) {
      toast.error(`Seleccione la dosis para: ${vacunasSinDosis.map(v => v.value).join(', ')}`);
      return;
    }

    // Validate family members if any
    for (const m of miembrosAtencion) {
      if (m.vacunas.length === 0) {
        toast.error(`Agregue al menos una vacuna para ${m.paciente.nombre_completo}`);
        return;
      }
      if (m.vacunas.some(v => !v.dosisSeleccionada)) {
        toast.error(`Seleccione la dosis de las vacunas de ${m.paciente.nombre_completo}`);
        return;
      }
      if (!m.paciente.fecha_nacimiento) {
        toast.error(`${m.paciente.nombre_completo} no tiene fecha de nacimiento registrada. Edítelo primero en Pacientes.`);
        return;
      }
    }

    // Prepare data with vaccines array including doses
    const vacunasConDosis = vacunasSeleccionadas.map(v => ({
      nombre: v.value,
      dosis: v.dosisSeleccionada,
      precio: v.precio || 0
    }));

    const submitData = {
      ...formData,
      vacunas: vacunasConDosis,
      vacuna: vacunasConDosis.map(v => `${v.nombre} (${v.dosis})`).join(', '),
      dosis: vacunasConDosis.map(v => v.dosis).join(', ')
    };

    setLoading(true);
    try {
      if (isEditing) {
        // If editing a draft, update via borrador endpoint; else legacy PUT
        const existing = originalRecord;
        if (existing?.estado === 'BORRADOR') {
          await axios.put(`${API}/vaccinations/borradores/${id}`, submitData, { withCredentials: true });
          toast.success('Borrador actualizado');
          navigate('/vacunaciones/pendientes');
        } else {
          await axios.put(`${API}/vaccinations/${id}`, submitData, { withCredentials: true });
          toast.success('Vacunación actualizada');
          navigate('/vaccinations');
        }
      } else {
        if (miembrosAtencion.length > 0) {
          // Family attention: titular + members in a single group with shared payment
          const miembrosPayload = [
            submitData,
            ...miembrosAtencion.map(m => {
              const vacs = m.vacunas.map(v => ({ nombre: v.value, dosis: v.dosisSeleccionada, precio: v.precio || 0 }));
              return {
                fecha_vacunacion: formData.fecha_vacunacion,
                rut: m.paciente.rut || '',
                pasaporte: m.paciente.pasaporte || '',
                otro_documento: m.paciente.otro_documento || '',
                nombres: m.paciente.nombres,
                primer_apellido: m.paciente.primer_apellido,
                segundo_apellido: m.paciente.segundo_apellido || '',
                fecha_nacimiento: m.paciente.fecha_nacimiento,
                comuna: m.paciente.comuna || formData.comuna || '',
                tipo_vacunacion: formData.tipo_vacunacion,
                dosis: vacs.map(v => v.dosis).join(', '),
                vacuna: vacs.map(v => `${v.nombre} (${v.dosis})`).join(', '),
                vacunas: vacs,
                telefono: m.paciente.telefono || formData.telefono || '',
                email: m.paciente.email || formData.email || '',
                monto: m.monto || 0,
                tipo_pago: formData.tipo_pago,
                numero_boleta: formData.numero_boleta,
                administracion: formData.administracion,
                comentarios: ''
              };
            })
          ];
          await axios.post(`${API}/vaccinations/borradores-familia`, { miembros: miembrosPayload }, {
            withCredentials: true
          });
          toast.success(`Atención familiar enviada a enfermería (${miembrosPayload.length} pacientes)`);
          navigate('/vacunaciones/pendientes');
        } else {
          // New vaccination → BORRADOR (cola enfermería) o RESERVADA (va a Reservas)
          const esReserva = /RESERVA/i.test(formData.tipo_vacunacion || '');
          await axios.post(`${API}/vaccinations/borradores`, submitData, {
            withCredentials: true
          });
          if (esReserva) {
            toast.success('Reserva registrada. La verás en la sección Reservas hasta que llegue la vacuna.');
            navigate('/reservas');
          } else {
            toast.success('Enviado a enfermería. La enfermera lo verá en su lista de pendientes.');
            navigate('/vacunaciones/pendientes');
          }
        }
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout>
      <div className="max-w-4xl mx-auto space-y-6 animate-fade-in" data-testid="new-vaccination-page">
        {/* Header */}
        <div className="flex items-center gap-4">
          <Button 
            variant="ghost" 
            onClick={() => navigate('/vaccinations')}
            data-testid="back-btn"
          >
            <ArrowLeft className="w-4 h-4" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-slate-900 heading">
              {isEditing ? 'Editar Vacunación' : 'Nueva Vacunación'}
            </h1>
            <p className="text-slate-500 mt-1">
              {isEditing ? 'Modifica los datos del registro' : 'Registra una nueva vacunación'}
            </p>
          </div>
        </div>

        <form onSubmit={handleSubmit}>
          {/* Patient Data */}
          <Card className="mb-6" data-testid="patient-card">
            <CardHeader>
              <CardTitle className="text-lg">Datos del Paciente</CardTitle>
            </CardHeader>
            <CardContent>
              {/* RNI Paste Field */}
              <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <Label htmlFor="rni-paste" className="text-amber-800 font-medium flex items-center gap-2">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    Pegar datos del RNI
                  </Label>
                  {rniParsed && (
                    <Badge className="bg-green-100 text-green-700">
                      ✓ Datos extraídos
                    </Badge>
                  )}
                </div>
                <Textarea
                  id="rni-paste"
                  value={rniText}
                  onChange={(e) => setRniText(e.target.value)}
                  placeholder="Pegue aquí los datos copiados del RNI y se autorellenarán los campos del paciente..."
                  className="min-h-[80px] bg-white border-amber-300 focus:border-amber-500"
                  data-testid="input-rni-paste"
                />
                <div className="flex gap-2 mt-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => parsearRNI(rniText)}
                    disabled={!rniText.trim()}
                    className="text-amber-700 border-amber-300 hover:bg-amber-100"
                  >
                    Extraer Datos
                  </Button>
                  {rniText && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={limpiarRNI}
                      className="text-slate-500"
                    >
                      Limpiar
                    </Button>
                  )}
                </div>
                <p className="text-xs text-amber-600 mt-2">
                  Copie los datos del paciente desde el RNI y péguelos aquí para autorellenar el formulario.
                </p>
              </div>

              {/* Patient fields grid */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <Label htmlFor="rut">RUT</Label>
                <div className="relative">
                  <Input
                    id="rut"
                    value={formData.rut}
                    onChange={(e) => handleChange('rut', e.target.value)}
                    placeholder="12345678-9"
                    className={`input-clinical pr-10 ${pacienteEncontrado ? 'border-green-500 bg-green-50' : ''}`}
                    data-testid="input-rut"
                  />
                  {buscandoPaciente && (
                    <div className="absolute right-3 top-1/2 -translate-y-1/2">
                      <Loader2 className="w-4 h-4 animate-spin text-blue-500" />
                    </div>
                  )}
                  {!buscandoPaciente && pacienteEncontrado && (
                    <div className="absolute right-3 top-1/2 -translate-y-1/2">
                      <svg className="w-4 h-4 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                  )}
                </div>
                {pacienteEncontrado && (
                  <p className="text-xs text-green-600 mt-1 flex items-center gap-1">
                    <span className="w-2 h-2 bg-green-500 rounded-full"></span>
                    Paciente encontrado en registros anteriores
                  </p>
                )}
              </div>
              <div>
                <Label htmlFor="pasaporte">Pasaporte</Label>
                <Input
                  id="pasaporte"
                  value={formData.pasaporte}
                  onChange={(e) => handleChange('pasaporte', e.target.value)}
                  placeholder="Número de pasaporte"
                  className="input-clinical"
                  data-testid="input-pasaporte"
                />
              </div>
              <div>
                <Label htmlFor="otro_documento">Otro Documento</Label>
                <Input
                  id="otro_documento"
                  value={formData.otro_documento}
                  onChange={(e) => handleChange('otro_documento', e.target.value)}
                  placeholder="Otro documento"
                  className="input-clinical"
                  data-testid="input-otro-documento"
                />
              </div>

              <div>
                <Label htmlFor="nombres">Nombres *</Label>
                <Input
                  id="nombres"
                  value={formData.nombres}
                  onChange={(e) => handleChange('nombres', e.target.value)}
                  placeholder="Nombres"
                  className="input-clinical"
                  required
                  data-testid="input-nombres"
                />
              </div>
              <div>
                <Label htmlFor="primer_apellido">Primer Apellido *</Label>
                <Input
                  id="primer_apellido"
                  value={formData.primer_apellido}
                  onChange={(e) => handleChange('primer_apellido', e.target.value)}
                  placeholder="Primer apellido"
                  className="input-clinical"
                  required
                  data-testid="input-primer-apellido"
                />
              </div>
              <div>
                <Label htmlFor="segundo_apellido">Segundo Apellido</Label>
                <Input
                  id="segundo_apellido"
                  value={formData.segundo_apellido}
                  onChange={(e) => handleChange('segundo_apellido', e.target.value)}
                  placeholder="Segundo apellido"
                  className="input-clinical"
                  data-testid="input-segundo-apellido"
                />
              </div>

              <div>
                <Label htmlFor="fecha_nacimiento">Fecha de Nacimiento *</Label>
                <Input
                  id="fecha_nacimiento"
                  type="date"
                  value={formData.fecha_nacimiento}
                  onChange={(e) => handleChange('fecha_nacimiento', e.target.value)}
                  className="input-clinical"
                  required
                  data-testid="input-fecha-nacimiento"
                />
                {/* Age display */}
                {edadCalculada && (
                  <div className="mt-2 p-2 bg-blue-50 border border-blue-200 rounded-lg">
                    <div className="flex items-center gap-2">
                      <span className="text-blue-600 font-medium text-sm">Edad:</span>
                      <span className="text-blue-800 font-bold">{edadCalculada.texto}</span>
                    </div>
                    {edadCalculada.años < 2 && (
                      <p className="text-xs text-blue-600 mt-1">
                        ({edadCalculada.totalMeses} meses en total)
                      </p>
                    )}
                  </div>
                )}
              </div>
              <div>
                <Label htmlFor="telefono">Teléfono</Label>
                <Input
                  id="telefono"
                  value={formData.telefono}
                  onChange={(e) => handleChange('telefono', e.target.value)}
                  placeholder="+56 9 1234 5678"
                  className="input-clinical"
                  data-testid="input-telefono"
                />
              </div>
              <div>
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleChange('email', e.target.value)}
                  placeholder="correo@ejemplo.com"
                  className="input-clinical"
                  data-testid="input-email"
                />
              </div>

              <div>
                <Label htmlFor="comuna">Comuna</Label>
                <Combobox
                  data-testid="select-comuna"
                  value={formData.comuna}
                  onChange={(value) => handleChange('comuna', value)}
                  options={catalogs.comunas.map(c => ({ value: c, label: c }))}
                  placeholder="Seleccionar comuna"
                  searchPlaceholder="Buscar comuna..."
                  emptyText="Sin coincidencias"
                  allowClear
                />
              </div>
              <div>
                <Label htmlFor="empresa">Empresa/Convenio</Label>
                <Combobox
                  data-testid="select-empresa"
                  value={formData.empresa_id || ''}
                  onChange={(value) => {
                    const selected = empresas.find(e => e.id === value);
                    handleChange('empresa_id', value);
                    handleChange('empresa', selected?.nombre || '');
                  }}
                  options={[
                    { value: 'none', label: 'Sin empresa' },
                    ...empresas.map(e => {
                      const extras = [];
                      if (e.rut) extras.push(e.rut);
                      if (e.tipo_convenio === 'DESCUENTO') extras.push(`${e.descuento_porcentaje}% desc.`);
                      if (e.tipo_convenio === 'PRECIO_FIJO') extras.push(`$${e.precio_fijo?.toLocaleString('es-CL')} fijo`);
                      const suffix = extras.length ? ` · ${extras.join(' · ')}` : '';
                      return {
                        value: e.id,
                        label: `${e.nombre}${suffix}`,
                      };
                    })
                  ]}
                  placeholder="Seleccionar empresa (opcional)"
                  searchPlaceholder="Buscar por nombre o RUT..."
                  emptyText="Sin coincidencias"
                  allowClear
                />
              </div>
              </div>
            </CardContent>
          </Card>

          {/* Vaccination Data */}
          <Card className="mb-6" data-testid="vaccination-card">
            <CardHeader>
              <CardTitle className="text-lg">Datos de Vacunación</CardTitle>
            </CardHeader>
            <CardContent className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <Label htmlFor="fecha_vacunacion">Fecha de Vacunación *</Label>
                <Input
                  id="fecha_vacunacion"
                  type="date"
                  value={formData.fecha_vacunacion}
                  onChange={(e) => handleChange('fecha_vacunacion', e.target.value)}
                  className="input-clinical"
                  required
                  data-testid="input-fecha-vacunacion"
                />
              </div>
              <div>
                <Label htmlFor="tipo_vacunacion">Tipo de Vacunación *</Label>
                <Combobox
                  data-testid="select-tipo-vacunacion"
                  value={formData.tipo_vacunacion}
                  onChange={(value) => handleChange('tipo_vacunacion', value)}
                  options={catalogs.tipo_vacunacion.map(t => ({ value: t, label: t }))}
                  placeholder="Seleccionar tipo"
                  searchPlaceholder="Buscar tipo..."
                  emptyText="Sin coincidencias"
                />
              </div>
              <div>
                <Label htmlFor="vacuna">Vacunas *</Label>
                <Combobox
                  data-testid="select-vacuna"
                  value=""
                  onChange={(value) => value && handleAddVacuna(value)}
                  options={vacunasConPrecio
                    .filter(v => !vacunasSeleccionadas.find(vs => vs.value === v.value))
                    .map(v => ({
                      value: v.value,
                      label: `${v.value}${v.precio ? ` - ${formatCurrency(v.precio)}` : ''}`
                    }))}
                  placeholder="Agregar vacuna..."
                  searchPlaceholder="Buscar vacuna..."
                  emptyText="Sin vacunas disponibles"
                />
                
                {/* Selected vaccines list with individual dose selectors */}
                {vacunasSeleccionadas.length > 0 && (
                  <div className="mt-3 space-y-3">
                    {vacunasSeleccionadas.map((v, idx) => {
                      // Get available doses for this vaccine
                      const dosisParaVacuna = v.dosis_disponibles && v.dosis_disponibles.length > 0 
                        ? v.dosis_disponibles 
                        : catalogs.dosis || [];
                      
                      return (
                        <div 
                          key={v.id} 
                          className="bg-blue-50 border border-blue-200 rounded-lg p-3"
                        >
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center gap-2">
                              <Badge variant="secondary" className="bg-blue-100">
                                {idx + 1}
                              </Badge>
                              <span className="text-sm font-medium">{v.value}</span>
                              <span className="text-sm text-emerald-600 font-medium">
                                {v.precio ? formatCurrency(v.precio) : ''}
                              </span>
                            </div>
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              className="h-6 w-6 p-0 text-red-500 hover:text-red-700 hover:bg-red-50"
                              onClick={() => handleRemoveVacuna(v.value)}
                            >
                              <X className="w-4 h-4" />
                            </Button>
                          </div>
                          
                          {/* Dose selector for this vaccine */}
                          <div className="flex items-center gap-2">
                            <span className="text-xs text-slate-600">Dosis:</span>
                            <Select 
                              value={v.dosisSeleccionada || ''} 
                              onValueChange={(dosis) => handleVacunaDosisChange(v.value, dosis)}
                            >
                              <SelectTrigger className="h-8 text-sm flex-1" data-testid={`select-dosis-${v.id}`}>
                                <SelectValue placeholder="Seleccionar dosis..." />
                              </SelectTrigger>
                              <SelectContent>
                                {dosisParaVacuna.map(d => (
                                  <SelectItem key={d} value={d}>{d}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            {v.dosisSeleccionada && (
                              <Badge className="bg-emerald-100 text-emerald-700 text-xs">
                                ✓
                              </Badge>
                            )}
                          </div>
                        </div>
                      );
                    })}
                    
                    {/* Total summary */}
                    <div className="flex items-center justify-between bg-emerald-50 border border-emerald-200 rounded-lg p-3 mt-3">
                      <div>
                        <span className="text-sm font-semibold text-emerald-800">
                          Total ({vacunasSeleccionadas.length} vacuna{vacunasSeleccionadas.length !== 1 ? 's' : ''}):
                        </span>
                        {!todasVacunasTienenDosis() && (
                          <p className="text-xs text-amber-600 mt-1">
                            ⚠ Seleccione la dosis para cada vacuna
                          </p>
                        )}
                      </div>
                      <span className="text-lg font-bold text-emerald-600">
                        {formatCurrency(totalSugerido)}
                      </span>
                    </div>
                  </div>
                )}
              </div>

              <div>
                <Label htmlFor="lote_serie">Lote/Serie (del inventario)</Label>
                {inventarioLotes.length > 0 ? (
                  <Combobox
                    data-testid="select-lote"
                    value={formData.inventario_id || ''}
                    onChange={(value) => {
                      const selected = inventarioLotes.find(l => l.id === value);
                      handleChange('inventario_id', value);
                      handleChange('lote_serie', selected?.lote || '');
                    }}
                    options={inventarioLotes.map(l => ({
                      value: l.id,
                      label: `${l.lote} - Stock: ${l.cantidad} - Vence: ${l.fecha_vencimiento}`
                    }))}
                    placeholder="Seleccionar lote disponible"
                    searchPlaceholder="Buscar lote..."
                    emptyText="Sin lotes"
                  />
                ) : (
                  <Input
                    id="lote_serie"
                    value={formData.lote_serie}
                    onChange={(e) => handleChange('lote_serie', e.target.value)}
                    placeholder={formData.vacuna ? "Sin stock disponible" : "Seleccione vacuna primero"}
                    className="input-clinical"
                    data-testid="input-lote-serie"
                  />
                )}
                {formData.vacuna && inventarioLotes.length === 0 && (
                  <p className="text-xs text-amber-600 mt-1">No hay lotes disponibles en inventario</p>
                )}
              </div>
              <div>
                <Label htmlFor="administracion">Administración</Label>
                <Combobox
                  data-testid="select-administracion"
                  value={formData.administracion}
                  onChange={(value) => handleChange('administracion', value)}
                  options={staff.map(s => ({
                    value: s.nombre_completo,
                    label: `${s.nombre_completo} (${s.cargo})`
                  }))}
                  placeholder="Seleccionar quien administra"
                  searchPlaceholder="Buscar personal..."
                  emptyText={staff.length === 0 ? "No hay personal registrado" : "Sin coincidencias"}
                />
              </div>

              <div>
                <Label htmlFor="registro_rni">Registro RNI</Label>
                <Input
                  id="registro_rni"
                  value={formData.registro_rni}
                  onChange={(e) => handleChange('registro_rni', e.target.value)}
                  placeholder="Número de registro"
                  className="input-clinical"
                  data-testid="input-registro-rni"
                />
              </div>
            </CardContent>
          </Card>

          {/* Family Attention Section */}
          {!isEditing && (
            <FamiliaAtencionSection
              familiaMiembros={familiaMiembros}
              miembros={miembrosAtencion}
              setMiembros={setMiembrosAtencion}
              vacunasConPrecio={vacunasConPrecio}
              dosisGenerales={catalogs.dosis || []}
              formatCurrency={formatCurrency}
              contactoTitular={{
                telefono: formData.telefono,
                email: formData.email,
                comuna: formData.comuna,
              }}
            />
          )}

          {/* Payment Data */}
          <Card className="mb-6" data-testid="payment-card">
            <CardHeader>
              <CardTitle className="text-lg">Datos de Pago</CardTitle>
              {miembrosAtencion.length > 0 && (
                <p className="text-xs text-purple-600">
                  El tipo de pago y la boleta aplican a toda la familia (un solo cobro).
                </p>
              )}
            </CardHeader>
            <CardContent className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <Label htmlFor="monto">Monto ($)</Label>
                <div className="flex gap-2">
                  <Input
                    id="monto"
                    type="number"
                    value={formData.monto}
                    onChange={(e) => handleChange('monto', parseFloat(e.target.value) || 0)}
                    placeholder="0"
                    className="input-clinical"
                    data-testid="input-monto"
                  />
                </div>
                {/* Suggested total from vaccines */}
                {totalSugerido > 0 && formData.monto !== totalSugerido && (
                  <div className="mt-2 p-2 bg-amber-50 border border-amber-200 rounded-lg">
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-amber-700">
                        <DollarSign className="w-3 h-3 inline mr-1" />
                        Total sugerido: <strong>{formatCurrency(totalSugerido)}</strong>
                      </span>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="h-6 text-xs text-amber-700 hover:text-amber-900 hover:bg-amber-100 px-2"
                        onClick={aplicarTotalSugerido}
                      >
                        Aplicar
                      </Button>
                    </div>
                  </div>
                )}
                {formData.monto === totalSugerido && totalSugerido > 0 && (
                  <p className="text-xs text-emerald-600 mt-1 flex items-center gap-1">
                    <span className="w-2 h-2 bg-emerald-500 rounded-full"></span>
                    Monto igual al total de vacunas
                  </p>
                )}
              </div>
              <div>
                <Label htmlFor="tipo_pago">Tipo de Pago</Label>
                <Combobox
                  data-testid="select-tipo-pago"
                  value={formData.tipo_pago}
                  onChange={(value) => handleChange('tipo_pago', value)}
                  options={catalogs.tipo_pago.map(t => ({ value: t, label: t }))}
                  placeholder="Seleccionar tipo"
                  searchPlaceholder="Buscar..."
                  emptyText="Sin coincidencias"
                />
              </div>
              <div>
                <Label htmlFor="numero_boleta">Nº de Boleta</Label>
                <Input
                  id="numero_boleta"
                  value={formData.numero_boleta}
                  onChange={(e) => handleChange('numero_boleta', e.target.value)}
                  placeholder="Número de boleta"
                  className="input-clinical"
                  data-testid="input-numero-boleta"
                />
              </div>
              {miembrosAtencion.length > 0 && (
                <div className="md:col-span-3 flex items-center justify-between bg-purple-50 border border-purple-200 rounded-lg p-3">
                  <span className="text-sm font-semibold text-purple-800">
                    Total familiar (titular + {miembrosAtencion.length} familiar{miembrosAtencion.length !== 1 ? 'es' : ''}):
                  </span>
                  <span className="text-lg font-bold text-purple-700" data-testid="total-familiar">
                    {formatCurrency((formData.monto || 0) + miembrosAtencion.reduce((s, m) => s + (m.monto || 0), 0))}
                  </span>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Comments */}
          <Card className="mb-6" data-testid="comments-card">
            <CardHeader>
              <CardTitle className="text-lg">Comentarios</CardTitle>
            </CardHeader>
            <CardContent>
              <Textarea
                value={formData.comentarios}
                onChange={(e) => handleChange('comentarios', e.target.value)}
                placeholder="Observaciones adicionales..."
                className="min-h-[100px]"
                data-testid="input-comentarios"
              />
            </CardContent>
          </Card>

          {/* Actions */}
          <div className="flex justify-end gap-3">
            <Button 
              type="button" 
              variant="outline"
              onClick={() => navigate('/vaccinations')}
              data-testid="cancel-btn"
            >
              Cancelar
            </Button>
            <Button 
              type="submit" 
              className="btn-accent gap-2"
              disabled={loading}
              data-testid="save-btn"
            >
              {loading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Save className="w-4 h-4" />
              )}
              {isEditing 
                ? (originalRecord?.estado === 'BORRADOR' ? 'Modificar borrador' : 'Actualizar')
                : 'Enviar a enfermería'}
            </Button>
          </div>
        </form>
      </div>
    </Layout>
  );
};

export default NewVaccinationPage;
